/**
 * HTTP request utilities for device unlock APIs
 * Handles brand-specific headers and authentication
 */

import { log } from '@/lib/utils/logger';

/**
 * Maps brand names to their corresponding identifiers.
 */
export const brandMapper: { [key: string]: string } = {
  totalwireless: 'TOTAL_WIRELESS',
  straighttalk: 'STRAIGHT_TALK',
  tracfone: 'TRACFONE',
  simplemobile: 'SIMPLE_MOBILE',
  familymobile: 'WFM'
};

/**
 * Makes a GET request to the specified URL with appropriate headers based on the brand.
 */
export const getAPI = async (url: string, brand: string, etoken?: string) => {
  const headers = {
    'Content-Type': 'application/json',
    channelId: 'DOTCOM',
    brand: brandMapper?.[brand] || brand.toUpperCase(),
    "x-auth-session": etoken ?? "",
    "x-client-lang": "ENG",
    "x-auth-env": "sitz"
  };
  
  log.debug('getAPI device-unlock', { headers, url, brand });
  
  return fetch(url, {
    method: 'GET',
    headers,
  }).then((res) => res.json());
};

/**
 * Makes a POST request to the specified URL with the provided body and headers.
 */
export const postAPI = async (url: string, body: Record<string, unknown>, brand: string, etoken?: string) => {
  const headers = {
    'Content-Type': 'application/json',
    channelId: 'DOTCOM',
    brand: brandMapper?.[brand] || brand.toUpperCase(),
    "x-auth-session": etoken ?? "",
    "x-client-lang": "ENG",
    "x-auth-env": "sitz"
  };
  
  log.debug('postAPI device-unlock', { headers, url, brand, body });
  
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
  }).then((res) => res.json());
};

/**
 * Makes a PUT request to the specified URL with the provided body and headers.
 */
export const putAPI = async (url: string, body: Record<string, unknown>, brand: string, etoken?: string) => {
  const headers = {
    'Content-Type': 'application/json',
    channelId: 'DOTCOM',
    brand: brandMapper?.[brand] || brand.toUpperCase(),
    "x-auth-session": etoken ?? "",
    "x-client-lang": "ENG",
    "x-auth-env": "sitz"
  };
  
  log.debug('putAPI device-unlock', { headers, url, brand, body });
  
  return fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers,
  }).then((res) => res.json());
};

/**
 * Generic API request function with customizable options
 */
export const apiRequest = async (
  url: string, 
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: Record<string, unknown>;
    brand: string;
    etoken?: string;
    customHeaders?: Record<string, string>;
  }
) => {
  const { method = 'GET', body, brand, etoken, customHeaders = {} } = options;
  
  const headers = {
    'Content-Type': 'application/json',
    channelId: 'DOTCOM',
    brand: brandMapper?.[brand] || brand.toUpperCase(),
    "x-auth-session": etoken ?? "",
    "x-client-lang": "ENG",
    "x-auth-env": "sitz",
    ...customHeaders
  };
  
  log.debug('apiRequest device-unlock', { headers, url, method, brand, body });
  
  const config: RequestInit = {
    method,
    headers,
  };
  
  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }
  
  return fetch(url, config).then((res) => res.json());
};